import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@nexus/ui";
import type { CompetitorKpiCharts, CompetitorKpiMetric } from "@nexus/contracts";
import {
  dashboardCompetitorChartQueryOptions,
  type CompetitorDashboardFilters,
} from "@/lib/queries/dashboard.queries";
import { useDashboardAsyncResource } from "../../hooks/useDashboardAsyncResource";
import { deriveCardState } from "../../lib/card-state";
import { CompetitorKpiTrendChart } from "../CompetitorKpiTrendChart/CompetitorKpiTrendChart";

interface CompetitorKpiTrendDrawerProps {
  tenantId: string | null | undefined;
  open: boolean;
  metric: CompetitorKpiMetric | null;
  metricLabel: string;
  filters: CompetitorDashboardFilters;
  filtersHash: string;
  onOpenChange: (open: boolean) => void;
}

function shouldKeepDrawerOpen(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("[data-dashboard-filter-bar]")
    || target.closest("[data-top-nav]")
    || target.closest("[data-competitor-kpi-card]"),
  );
}

export function CompetitorKpiTrendDrawer({
  tenantId,
  open,
  metric,
  metricLabel,
  filters,
  filtersHash,
  onOpenChange,
}: CompetitorKpiTrendDrawerProps) {
  const { t } = useTranslation();
  const resetKey = `${tenantId ?? ""}:${filtersHash}`;

  const chartOptions = dashboardCompetitorChartQueryOptions(
    open ? (tenantId ?? null) : null,
    filters,
  );

  const { data: chartSummary } = useQuery({
    ...chartOptions,
    enabled: open && !!tenantId,
  });

  const chartResource = useDashboardAsyncResource<CompetitorKpiCharts>({
    summary: chartSummary,
    targetKey: chartOptions.queryKey,
    resultField: "charts",
    resetKey,
    enabled: open && !!tenantId,
  });

  const chartState = deriveCardState(
    chartResource.timedOut,
    chartResource.status,
    chartResource.data,
  );

  const points = useMemo(() => {
    if (!metric || !chartResource.data) return [];
    return chartResource.data[metric] ?? [];
  }, [metric, chartResource.data]);

  const brandLabels = chartResource.data?.brandLabels;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        className="flex w-full flex-col overflow-hidden bg-card p-0 sm:max-w-[720px]"
        onPointerDownOutside={(e) => {
          if (shouldKeepDrawerOpen(e.target)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (shouldKeepDrawerOpen(e.target)) e.preventDefault();
        }}
      >
        <SheetHeader className="border-b px-6 pb-4 pt-6">
          <div className="pl-10">
            <SheetTitle className="text-base font-medium">
              {t("dashboard.competitorTrendDrawer.title", {
                metric: metricLabel,
                defaultValue: "{{metric}} trends and learnings",
              })}
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {chartState === "loading" && (
            <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
              {t("dashboard.competitorTrendDrawer.loading")}
            </div>
          )}

          {chartState === "error" && (
            <div className="flex h-[280px] items-center justify-center text-sm text-destructive">
              {t("dashboard.competitorTrendDrawer.error")}
            </div>
          )}

          {chartState === "empty" && (
            <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
              {t("dashboard.competitorTrendDrawer.empty")}
            </div>
          )}

          {chartState === "ready" && metric && (
            <CompetitorKpiTrendChart
              points={points}
              youLabel={brandLabels?.you ?? t("dashboard.competitorTrendDrawer.you")}
              mainCompetitorLabel={
                brandLabels?.mainCompetitor ?? t("dashboard.competitorTrendDrawer.mainCompetitor")
              }
              benchmarkLabel={t("dashboard.competitorTrendDrawer.benchmark")}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
